import React, {
  useState,
  memo,
  ReactElement,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import T from 'i18n-react';
import moment from 'moment';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import FormHelperText from '@material-ui/core/FormHelperText';

import type { PlanTermWorkingTimeProps } from '../../../../../core/Plans/context';
import TimeRangeTextGroup from './TimeRangeTextGroup';
import Select from '../../../../../core/Plans/Field/Select';
import AppDialog from '../../../../../core/Dialog/AppDialog';
import {
  workingTimeMinutes,
  translateMinutes,
  convertValue,
  ONE_DAY,
} from '../../hooks/useWorkingTime';

const useStyles = makeStyles((theme) => ({
  container: {
    flexWrap: 'wrap',
    paddingTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
      paddingTop: 0,
    },
  },
  task: {
    [theme.breakpoints.up('md')]: {
      maxWidth: 300,
    },
  },
  separator: {
    [theme.breakpoints.up('sm')]: {
      display: 'inline-block',
    },
  },
  delimiter: {
    margin: theme.spacing(0, 0.5),
  },
  error: {
    color: theme.palette.error.main,
  },
  deleteButton: {
    marginLeft: theme.spacing(2),
  },
}));

export type WorkingTimeChangeData = PlanTermWorkingTimeProps & {
  index: number;
  startTimeValidity?: boolean;
  endTimeValidity?: boolean;
  workingTimeValidity?: boolean;
};

export type WorkingTimeProps = PlanTermWorkingTimeProps & {
  index?: number;
  onChange?: (data: WorkingTimeChangeData) => void;
  onDelete?: (index: number) => void;
  startTimeValidity?: boolean;
  endTimeValidity?: boolean;
  workingTimeValidity?: boolean;
};

export type MenuItemProps = {
  value: string;
  disabled: boolean;
  children: ReactElement;
};

const WorkingTime = memo(
  ({
    index,
    onChange,
    onDelete,
    startTime,
    endTime,
    workingTime,
    startTimeValidity,
    endTimeValidity,
    workingTimeValidity,
    workingHours,
  }: WorkingTimeProps) => {
    const classes = useStyles();

    const dialogRef = useRef<AppDialog>(null);
    const [changeStartTime, setChangeStartTime] = useState<string | undefined>(
      startTime,
    );
    const [changeEndTime, setChangeEndTime] = useState<string | undefined>(
      endTime,
    );
    const [changeWorkingTime, setChangeWorkingTime] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleStartTimeChange = useCallback(
      (event) => {
        setChangeStartTime((prevChangeStartTime) => {
          const value = convertValue(event.target.value, prevChangeStartTime);
          onChange({
            index,
            startTime: moment(value, 'HH:mm'),
          });
          return value;
        });
      },
      [index, onChange],
    );

    const handleEndTimeChange = useCallback(
      (event) => {
        setChangeEndTime((prevChangeEndTime) => {
          const value = convertValue(event.target.value, prevChangeEndTime);
          onChange({
            index,
            endTime: moment(value, 'HH:mm'),
          });
          return value;
        });
      },
      [index, onChange],
    );

    const handleWorkingTimeChange = useCallback(
      (event) => {
        setChangeWorkingTime(event.target.value);
        onChange({
          index,
          workingTime: event.target.value,
        });
      },
      [index, onChange],
    );

    const handleDeleteClick = useCallback(() => {
      if (startTime || endTime) {
        dialogRef.current?.handleOpen?.();
      } else {
        onDelete?.(index);
      }
    }, [index, startTime, endTime, onDelete]);

    const handleDelete = useCallback(
      (event, onSuccess, onError) => {
        onSuccess({
          callback: () => onDelete?.(index),
        });
      },
      [index, onDelete],
    );

    useEffect(() => {
      if (index === undefined) {
        return;
      }
      const checkStartTime = Number(changeStartTime?.replace(':', ''));
      const checkEndTime = Number(changeEndTime?.replace(':', ''));
      const checkWorkingTime = Number(
        (changeWorkingTime || workingTime).replace(':', ''),
      );
      const checkWorkingHoursStartTime = Number(
        workingHours?.startTime.replace(':', ''),
      );
      const checkWorkingHoursEndTime = Number(
        workingHours?.endTime.replace(':', ''),
      );
      const isToday = checkWorkingHoursEndTime < 2400;
      const isInputStartTime = !!changeStartTime?.match(
        /^[0-2][0-9]:[0-5]{2}$|^[0-9]:[0-5][0-9]$/,
      );
      const isInputEndTime = !!changeEndTime?.match(
        /^[0-2][0-9]:[0-5]{2}$|^[0-9]:[0-5][0-9]$/,
      );
      const isInputWorkingTime = isInputStartTime && isInputEndTime;

      let startTimeValidity = isInputStartTime && checkStartTime < ONE_DAY;
      let endTimeValidity = isInputEndTime && checkEndTime < ONE_DAY;
      let workingTimeValidity = checkWorkingTime > 0 && checkWorkingTime < 801;

      if (checkWorkingTime > checkEndTime - checkStartTime) {
        startTimeValidity = false;
        endTimeValidity = false;
        workingTimeValidity = false;
      }
      if (isInputWorkingTime && checkEndTime <= checkStartTime) {
        startTimeValidity = false;
        endTimeValidity = false;
        setErrorMessage(
          T.translate('planPage.planTerms.error.invalidWorkingTime') as string,
        );
      } else if (
        (checkWorkingHoursStartTime > checkStartTime && isToday) ||
        (checkEndTime > checkWorkingHoursEndTime && isToday)
      ) {
        startTimeValidity = false;
        endTimeValidity = false;
        setErrorMessage(
          T.translate('planPage.planTerms.error.invalidOverTime') as string,
        );
      } else {
        setErrorMessage('');
      }
      onChange({
        index,
        startTimeValidity: startTimeValidity,
        endTimeValidity: endTimeValidity,
        workingTimeValidity: workingTimeValidity,
      });
    }, [changeStartTime, changeEndTime, changeWorkingTime, workingHours]);

    const allowDelete = typeof onDelete === 'function';

    return (
      <Grid container spacing={1} direction="column">
        <Grid item>
          <Grid container alignItems="center" className={classes.container}>
            <TimeRangeTextGroup
              startTimeText={changeStartTime}
              endTimeText={changeEndTime}
              startTimePlaceholder={
                T.translate('planPage.planTerms.startTime') as string
              }
              endTimePlaceholder={
                T.translate('planPage.planTerms.endTime') as string
              }
              toolTipText={T.translate('textFieldTooltip.time') as string}
              startTimeError={startTimeValidity === false}
              endTimeError={endTimeValidity === false}
              onChangeStartTimeText={handleStartTimeChange}
              onChangeEndTimeText={handleEndTimeChange}
            />
            <Grid item className={classes.separator}>
              <Typography>
                {T.translate('planPage.planTerms.scheduleRelation')}
              </Typography>
            </Grid>
            <Grid item xs={12} md="auto" className={classes.task}>
              <Select
                selectProps={{
                  value: workingTime,
                  displayEmpty: true,
                  onChange: handleWorkingTimeChange,
                  error: workingTimeValidity === false,
                }}
                menuItemsProps={[
                  {
                    value: '',
                    disabled: true,
                    children: (
                      <Tooltip
                        title={
                          T.translate(
                            'selectFieldTooltip.totalWorkingTime',
                          ) as string
                        }
                      >
                        <Typography
                          component="span"
                          color="textSecondary"
                          variant="inherit"
                        >
                          {T.translate('planPage.workingTimes.totalTime')}
                        </Typography>
                      </Tooltip>
                    ),
                  },
                ].concat(
                  Object.values(workingTimeMinutes).map((workingMinutes) => ({
                    value: moment('2000/01/01 00:00:00')
                      .add(workingMinutes, 'm')
                      .format('HH:mm'),
                    children: translateMinutes(workingMinutes),
                  })) as MenuItemProps[],
                )}
              />
            </Grid>
            <Grid item className={classes.separator}>
              <Typography>
                {T.translate('planPage.planTerms.workTime')}
              </Typography>
            </Grid>
            {allowDelete && (
              <Grid item className={classes.deleteButton}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleDeleteClick}
                >
                  {T.translate('delete')}
                </Button>
                <AppDialog
                  disableDialogTitle
                  contentNode={
                    <>
                      <Typography gutterBottom>
                        {T.translate('planPage.planTerms.deleteWorkingTime')}
                      </Typography>
                      <WorkingTime
                        startTime={startTime}
                        endTime={endTime}
                        workingTime={workingTime}
                      />
                    </>
                  }
                  DialogProps={{ maxWidth: 500 }}
                  actionButtonText={T.translate('delete')}
                  onAccept={handleDelete}
                  ref={dialogRef}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        {errorMessage.length > 0 && (
          <FormHelperText className={classes.error}>
            {errorMessage}
          </FormHelperText>
        )}
        <Grid item>
          <Grid container spacing={1} justify="center"></Grid>
        </Grid>
      </Grid>
    );
  },
);

export default WorkingTime;
